// External Dependencies:
import React from 'react'
import { connect } from 'react-redux'
import YouTubePlayer from 'react-player/lib/players/YouTube'
import VimeoPlayer from 'react-player/lib/players/Vimeo'
import FilePlayer from 'react-player/lib/players/FilePlayer'
import screenfull from 'screenfull'

// Internal Dependencies:
import { getHhMmSsFromSeconds } from 'utilities/time'
import { getUrlParameter } from 'utilities/url'

// Events:
const videoEnded = document.createEvent( 'Event' )
videoEnded.initEvent( 'videoEnded', true, true )

class MediaPlayer extends React.Component {
	static defaultProps = {
		url: '',
		id: '',
		service: ''
	}

	constructor( props ) {
		super( props )

		let service = props.service

		if ( ! service && props.url && props.url.indexOf( 'vimeo' ) > -1 ) {
			service = 'vimeo'
		} else if ( ! service && props.url && props.url.indexOf( 'youtube' ) > -1 ) {
			service = 'youtube'
		}

		this.state = {
			hasStarted: false,
			isPlaying: false,
			vimeoStarted: false,
			hasFinished: false,
			played: 0,
			playedSeconds: 0,
			loaded: 0,
			duration: 0,
			service: service,
			isFullscreen: false,
			mouseIsHidden: false
		}

		this.playerRef = React.createRef()
		this.mousemoveTimeout = null
	}

	componentDidMount() {
		document.addEventListener( 'mousemove', this.handleMouseMove )

		screenfull.on( 'change', () => {
			this.setState( {
				isFullscreen: screenfull.isFullscreen,
				mouseIsHidden: ! screenfull.isFullscreen
			} )
		} )
	}

	componentWillUnmount() {
		document.removeEventListener( 'mousemove', this.handleMouseMove )
	}

	componentWillReceiveProps( nextProps ) {
		const { url } = this.props

		if ( url != nextProps.url ) {
			this.setState( {
				isPlaying: false,
				played: 0,
				loaded: 0,
				duration: 0
			} )
		}
	}

	handleMouseMove = () => {
		const { isFullscreen, mouseIsHidden } = this.state

		if ( ! isFullscreen ) {
			return
		}

		if ( mouseIsHidden ) {
			this.setState( {
				mouseIsHidden: false
			} )
		}

		clearTimeout( this.mousemoveTimeout )

		this.mousemoveTimeout = setTimeout( () => {
			this.setState( {
				mouseIsHidden: true
			} )
		}, 3000 )
	}

	handleWrapClick = ( e ) => {
		if ( this.wrap == e.target ) {
			this.setState( {
				isPlaying: ! this.state.isPlaying
			} )
		}
	}

	handleClick = ( e ) => {
		this.setState({
			hasStarted: true,
			isPlaying: ! this.state.isPlaying
		})
	}

	onPause = () => {
		this.setState( {
			isPlaying: false
		} )
	}

	onEnded = () => {
		this.setState( {
			isPlaying: false,
			hasFinished: true
		} )

		window.dispatchEvent( videoEnded )
	}

	onProgress = ( state ) => {
		const { vimeoStarted, duration, service } = this.state

		if ( ! this.state.seeking ) {
			this.setState( state )
		}

		if ( 'vimeo' == service && ! vimeoStarted ) {
			this.startVimeo()
		}

		this.setState( {
			timeRemainingFormatted: getHhMmSsFromSeconds( duration - state.playedSeconds )
		} )
	}

	// Use background mode to hide the controls for vimeo.
	// Background will autoplay and mute - stop it.
	startVimeo = () => {
		const { hasStarted } = this.state

		if ( hasStarted || ! this.playerRef ) {
			return
		}

		let internalPlayer = this.playerRef.getInternalPlayer()

		if ( ! internalPlayer ) {
			return
		}

		// Set volume:
		internalPlayer.setVolume( 1 ).then( () => {
			// Pause:
			internalPlayer.pause().then( () => {
				internalPlayer.getPaused().then( ( paused ) => {
					if ( paused ) {
						// Rewind:
						internalPlayer.setCurrentTime( 0.0 ).then( () => {
							// Pause again:
							internalPlayer.pause().then( () => {
								this.setState( {
									vimeoStarted: true
								} )
							} )

							internalPlayer.setLoop( false )
						} )
					}
				} )
			} )
		} )
	}

	onDuration = ( duration ) => {
		this.setState( {
			duration: duration,
			timeRemainingFormatted: getHhMmSsFromSeconds( duration )
		} )
	}

	handleSeekMouseDown = ( e ) => {
		this.setState( {
			seeking: true
		} )
	}

	handleSeekChange = ( e ) => {
		const { played } = this.state
		const value = parseFloat( e.target.value )

		this.setState( {
			played: parseFloat( value )
		} )
	}

	handleSeekMouseUp = ( e ) => {
		const { played } = this.state
		const value = parseFloat( e.target.value )

		this.setState( {
			seeking: false
		} )

		this.playerRef.seekTo( value )
	}

	setSeek = ( value ) => {

	}

	handlePlay = () => {
		const { service } = this.state

		this.setState( {
			isPlaying: true,
			hasStarted: true
		} )

		if ( 'youtube' == service ) {
			this.setState( {
				showYouTubeCover: true
			} )

			setTimeout( () => {
				this.setState( {
					showYouTubeCover: false
				} )
			}, 800 )
		}

		if ( 'vimeo' == service ) {
			let internalPlayer = this.playerRef.getInternalPlayer()

			if ( internalPlayer ) {
				internalPlayer.play()
			}
		}
	}

	handlePause = () => {
		const { service } = this.state

		this.setState( {
			isPlaying: false
		} )

		if ( 'vimeo' == service ) {
			let internalPlayer = this.playerRef.getInternalPlayer()

			if ( internalPlayer ) {
				internalPlayer.pause()
			}
		}
	}

	handleFullscreenEnter = () => {
		if ( ! screenfull.isEnabled ) {
			return
		}

		screenfull.request( this.wrap )
	}

	handleFullscreenExit = () => {
		if ( ! screenfull.isEnabled ) {
			return
		}

		screenfull.exit()
	}

	render() {
		let { wrapStyle, thumb_url, id, url, file, type, width, height, aspectRatio } = this.props
		let { hasStarted, isPlaying, service, played, loaded, timeRemainingFormatted, isFullscreen, mouseIsHidden, showYouTubeCover } = this.state
		let wrapClass = isPlaying ? 'media-player is-playing' : 'media-player is-not-playing'
		wrapClass += ` is-service-${service}`

		if ( hasStarted ) {
			wrapClass += ' has-started'
		}

		if ( isFullscreen ) {
			wrapClass += ' is-fullscreen'
		}

		if ( isFullscreen && mouseIsHidden ) {
			wrapClass += ' is-mouse-hidden'
		}

		if ( ! isPlaying || showYouTubeCover ) {
			wrapClass += ' with-youtube-cover'
		}

		if ( 'audio' == type ) {
			wrapClass += ' audio-player'
		}

		let video,
			showControls = false

		if ( ! url ) {
			if ( id && 'youtube' == service ) {
				url = `https://www.youtube.com/watch?v=${id}`
			} else if ( id && 'vimeo' == service ) {
				url = `https://vimeo.com/${id}`
			}
		}

		if ( url ) {
			if ( 'vimeo' == service ) {
				showControls = true
				video = (
					<VimeoPlayer
						ref={ ( e ) => { this.playerRef = e } }
						playing={ isPlaying }
						url={ url }
						controls={ false }
						onPause={ this.onPause }
						onEnded={ this.onEnded }
						onProgress={ this.onProgress }
						onDuration={ this.onDuration }
						config={ {
							vimeo: {
								playerOptions: {
									background: true
								}
							}
						} }
					/>
				)
			} else if ( 'youtube' == service ) {
				showControls = true
				video = (
					<YouTubePlayer
						ref={ ( e ) => { this.playerRef = e } }
						playing={ isPlaying }
						url={ url }
						controls={ false }
						onPause={ this.onPause }
						onEnded={ this.onEnded }
						onProgress={ this.onProgress }
						onDuration={ this.onDuration }
						config={ {
							youtube: {
								playerVars: {
									controls: 0,
									modestbranding: 1,
									rel: 0
								}
							}
						} }
					/>
				)
			}
		} else if ( file ) {
			let forceAudio = 'audio' == type ? true : false
			showControls = 'audio' == type ? false : true

			video = (
				<FilePlayer
					ref={ ( e ) => { this.playerRef = e } }
					playing={ isPlaying }
					url={ file }
					controls={ false }
					onPause={ this.onPause }
					onEnded={ this.onEnded }
					onProgress={ this.onProgress }
					onDuration={ this.onDuration }
				/>
			)
		}

		return (
			<div className={ wrapClass } style={ wrapStyle } onClick={ this.handleWrapClick } ref={ ( e ) => { this.wrap = e } }>
				{ video }

				{ showControls && thumb_url &&
					<div className="-poster">
						<img src={ thumb_url } alt="" />
					</div>
				}

				{ showControls &&
					<button className="-play-wrap" onClick={ this.handlePlay }>
						<div className="-play">
							<svg viewBox="0 0 18 18">
								<path d="M15.562 8.1L3.87.225C3.052-.337 2 .225 2 1.125v15.75c0 .9 1.052 1.462 1.87.9L15.563 9.9c.584-.45.584-1.35 0-1.8z"></path>
							</svg>
						</div>
					</button>
				}

				{ hasStarted && showControls &&
					<div className="-controls">
						<div className="-inner">
							<div className="-play-pause">
								<button className="-play" onClick={ this.handlePlay }>
									<svg viewBox="0 0 18 18">
										<path d="M15.562 8.1L3.87.225C3.052-.337 2 .225 2 1.125v15.75c0 .9 1.052 1.462 1.87.9L15.563 9.9c.584-.45.584-1.35 0-1.8z"></path>
									</svg>
								</button>

								<button className="-pause" onClick={ this.handlePause }>
									<svg viewBox="0 0 18 18">
										<rect x="0" y="0" rx="2" ry="2" height="16" width="5" />
										<rect x="8" y="0" rx="2" ry="2" height="16" width="5" />
									</svg>
								</button>
							</div>

							<div className="-track-wrap">
								<input
									className="-input"
									type="range" min={ 0 } max={ 1 } step="any"
									value={ played }
									onMouseDown={ this.handleSeekMouseDown }
									onTouchStart={ this.handleSeekMouseDown }
									onChange={ this.handleSeekChange }
									onTouchMove={ this.handleSeekChange }
									onMouseUp={ this.handleSeekMouseUp }
									onTouchEnd={ this.handleSeekMouseUp }
								/>

								<div className="-progress">
									<div className="-progress-played" style={ { width: `${played * 100}%` } } max="100"></div>
									<div className="-progress-buffer" style={ { width: `${loaded * 100}%` } } max="100"></div>
								</div>
							</div>

							<div className="-time">
								<span>{ timeRemainingFormatted }</span>
							</div>

							<div className="-fullscreen">
								<button className="-enter-fullscreen" onClick={ this.handleFullscreenEnter }>
									<svg viewBox="0 0 18 18">
										<path d="M10 3h3.6l-4 4L11 8.4l4-4V8h2V1h-7zM7 9.6l-4 4V10H1v7h7v-2H4.4l4-4z"></path>
									</svg>
								</button>

								<button className="-exit-fullscreen" onClick={ this.handleFullscreenExit }>
									<svg viewBox="0 0 18 18">
										<path d="M1 12h3.6l-4 4L2 17.4l4-4V17h2v-7H1zM16 .6l-4 4V1h-2v7h7V6h-3.6l4-4z"></path>
									</svg>
								</button>
							</div>
						</div>
					</div>
				}
			</div>
		)
	}
}

export default MediaPlayer



// WEBPACK FOOTER //
// ./components/MediaPlayer/MediaPlayer.jsx